import React, { memo, useState, useEffect, useRef } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import useSimulationStore from '../../../store/simulationStore';

interface EnergyGraphChartProps {
    height?: number;
}

interface EnergyDataPoint {
    time: number;
    total: number;
    translational: number;
    rotational: number;
}

const MAX_DATA_POINTS = 100;
const UPDATE_INTERVAL = 500; // 2Hz update rate

const EnergyGraphChart: React.FC<EnergyGraphChartProps> = memo(({ height = 250 }) => {
    // Individual subscriptions for performance
    const totalEnergy = useSimulationStore(state => state.totalEnergy);
    const translationalEnergy = useSimulationStore(state => state.translationalEnergy);
    const rotationalEnergy = useSimulationStore(state => state.rotationalEnergy);
    const initialTotalEnergy = useSimulationStore(state => state.initialTotalEnergy);
    const isRunning = useSimulationStore(state => state.isRunning);

    const [energyHistory, setEnergyHistory] = useState<EnergyDataPoint[]>([]);
    const timeRef = useRef(0);
    const lastUpdateRef = useRef(0);

    // Throttled update effect
    useEffect(() => {
        if (!isRunning) return;

        const interval = setInterval(() => {
            const now = Date.now();
            if (now - lastUpdateRef.current < UPDATE_INTERVAL) return;
            lastUpdateRef.current = now;

            timeRef.current += UPDATE_INTERVAL / 1000;
            setEnergyHistory(prev => {
                const newPoint = {
                    time: timeRef.current,
                    total: totalEnergy,
                    translational: translationalEnergy,
                    rotational: rotationalEnergy,
                };
                const updated = [...prev, newPoint];
                return updated.length > MAX_DATA_POINTS
                    ? updated.slice(-MAX_DATA_POINTS)
                    : updated;
            });
        }, UPDATE_INTERVAL);

        return () => clearInterval(interval);
    }, [isRunning, totalEnergy, translationalEnergy, rotationalEnergy]);

    // Reset on simulation reset
    useEffect(() => {
        if (!isRunning && energyHistory.length > 0 && totalEnergy === 0) {
            setEnergyHistory([]);
            timeRef.current = 0;
        }
    }, [isRunning, totalEnergy, energyHistory.length]);

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Summary */}
            <div style={summaryStyle}>
                <span>Current: <strong style={{ color: '#4fc3f7' }}>{totalEnergy.toFixed(2)}</strong></span>
                <span>Initial: <strong style={{ color: '#81c784' }}>{initialTotalEnergy.toFixed(2)}</strong></span>
                <span>Points: <strong style={{ color: '#ffb74d' }}>{energyHistory.length}</strong></span>
            </div>

            {/* Chart */}
            <div style={{ flex: 1, minHeight: 0 }}>
                {energyHistory.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={energyHistory} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis
                                dataKey="time"
                                stroke="#888"
                                tick={{ fill: '#ccc', fontSize: 10 }}
                                tickFormatter={(v) => `${v.toFixed(0)}s`}
                            />
                            <YAxis
                                stroke="#888"
                                tick={{ fill: '#ccc', fontSize: 10 }}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'rgba(30, 30, 30, 0.95)',
                                    border: '1px solid #4fc3f7',
                                    borderRadius: '4px',
                                    color: '#fff'
                                }}
                                formatter={(value: number) => value.toFixed(3)}
                                labelFormatter={(label) => `Time: ${Number(label).toFixed(1)}s`}
                            />
                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                            <ReferenceLine y={initialTotalEnergy} stroke="#666" strokeDasharray="5 5" />
                            <Line
                                type="monotone"
                                dataKey="total"
                                stroke="#4fc3f7"
                                strokeWidth={2}
                                dot={false}
                                name="Total"
                                isAnimationActive={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="translational"
                                stroke="#81c784"
                                strokeWidth={1}
                                dot={false}
                                name="Translational"
                                isAnimationActive={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="rotational"
                                stroke="#ffb74d"
                                strokeWidth={1}
                                dot={false}
                                name="Rotational"
                                isAnimationActive={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={emptyStateStyle}>
                        <span>Collecting energy data...</span>
                        <span style={{ fontSize: '10px', color: '#666' }}>Start simulation to see energy over time</span>
                    </div>
                )}
            </div>
        </div>
    );
});

EnergyGraphChart.displayName = 'EnergyGraphChart';

const summaryStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: 'rgba(40, 40, 40, 0.5)',
    borderRadius: '4px',
    marginBottom: '8px',
    fontSize: '11px',
    color: '#ccc',
    fontFamily: 'monospace',
};

const emptyStateStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#888',
    fontSize: '12px',
    gap: '8px',
};

export default EnergyGraphChart;
